"use client";

import React from "react";
import styled, { css } from "styled-components";
import { ButtonVariant, MarketingButtonProps } from "../utils/types";
import { buttonStyles } from "./styles";
import { BUTTON_CONFIG } from "../utils/constants";
import Link from "next/link";

type ButtonProps = {
  $variant: ButtonVariant;
  $small: boolean;
};

const styling = css<ButtonProps>`
  ${buttonStyles}
  border: none;
  cursor: pointer;
  pointer-events: auto;
  z-index: 1;
  ${props => {
    const size = props.$small ? "small" : "regular";
    const variant = BUTTON_CONFIG.variants[props.$variant];
    const sizeConfig = BUTTON_CONFIG.sizes[size];

    return `
      padding: ${sizeConfig.padding};
      border-radius: ${sizeConfig.borderRadius};
      font-size: ${sizeConfig.fontSize};
      background-color: ${variant.backgroundColor};
      color: ${variant.color};
      border: ${variant.border};
      &:hover:not(:disabled),
      &:focus-visible:not(:disabled) {
        background-color: ${variant.hoverBackgroundColor};
        text-decoration: none;
        color: ${variant.hoverColor ?? variant.color};
      }
    `;
  }}

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
  &:focus {
    outline: none;
  }
`;

const BaseMarketingButton = styled.button<ButtonProps>`
  ${styling}
`;

const BaseMarketingLink = styled(Link)<ButtonProps>`
  ${styling}
`;

export const MarketingButton: React.FC<MarketingButtonProps> = ({
  href,
  target,
  rel,
  onClick,
  disabled,
  type = "button",
  variant = "blue",
  small = false,
  children,
  className,
  style,
}) => {
  const commonProps = {
    $variant: variant,
    $small: small,
    onClick: disabled ? undefined : onClick,
    disabled,
    className,
    style,
  };

  if (href) {
    return (
      <BaseMarketingLink
        {...commonProps}
        href={href}
        target={target}
        rel={target === "_blank" && !rel ? "noopener noreferrer" : rel}>
        {children}
      </BaseMarketingLink>
    );
  }

  return (
    <BaseMarketingButton {...commonProps} type={type}>
      {children}
    </BaseMarketingButton>
  );
};
